import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { setCartItem } from "../../redux/features/cartSlice";
import {
  useAddQuoteToCartMutation,
  useCalculateInstantQuoteMutation,
  useCreateQuoteMutation,
  useGetInstantQuoteConfigQuery,
  useSubmitQuoteMutation,
  useUpdateQuoteMutation,
} from "../../redux/api/quoteApi";

const DEFAULT_FORM_VALUES = {
  quoteType: "standard_pcb",
  customerInfo: {
    name: "",
    email: "",
    phone: "",
    company: "",
  },
  shipping: {
    countryCode: "US",
  },
  pcbSpec: {
    boardType: "single_pieces",
    differentDesignCount: 1,
    size: {
      length: 100,
      width: 80,
      unit: "mm",
    },
    quantity: 10,
    layers: 2,
    material: "FR4",
    fr4Tg: "TG150",
    thickness: 1.6,
    thicknessUnit: "mm",
    minTrackSpacing: "6/6mil",
    minHoleSize: "0.3mm",
    solderMask: "green",
    silkscreen: "white",
    surfaceFinish: "hasl_lead_free",
    viaProcess: "tenting_vias",
    finishedCopper: "1oz",
    specialRequest: "",
    advancedOptions: {
      edgeConnector: false,
      impedanceControl: false,
      castellatedHoles: false,
      edgePlating: false,
      customStackup: false,
      viaInPad: false,
      resinFilledVias: false,
    },
  },
};

/**
 * 获取或创建当前浏览器会话的报价 Session ID。
 * @returns {string} 稳定的会话 ID
 */
const getQuoteSessionId = () => {
  const storageKey = "instant_quote_session_id";
  const existingId = window.localStorage.getItem(storageKey);
  if (existingId) {
    return existingId;
  }

  const nextId = `quote-session-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
  window.localStorage.setItem(storageKey, nextId);
  return nextId;
};

/**
 * 即时报价表单组件：负责参数录入、在线计算、保存报价和加入购物车。
 */
function FormGetQuote() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [formValues, setFormValues] = useState(DEFAULT_FORM_VALUES);
  const [attachments, setAttachments] = useState([]);
  const [savedQuote, setSavedQuote] = useState(null);
  const [quoteResult, setQuoteResult] = useState(null);
  const [quoteDirty, setQuoteDirty] = useState(true);
  const sessionId = useMemo(() => getQuoteSessionId(), []);

  const { data: configResponse, isLoading: isConfigLoading } =
    useGetInstantQuoteConfigQuery();
  const [calculateInstantQuote, { isLoading: isCalculating }] =
    useCalculateInstantQuoteMutation();
  const [createQuote, { isLoading: isCreatingQuote }] = useCreateQuoteMutation();
  const [updateQuote, { isLoading: isUpdatingQuote }] = useUpdateQuoteMutation();
  const [submitQuote, { isLoading: isSubmittingQuote }] = useSubmitQuoteMutation();
  const [addQuoteToCart, { isLoading: isAddingQuoteToCart }] =
    useAddQuoteToCartMutation();

  const config = configResponse?.config || {};
  const isSavingQuote = isCreatingQuote || isUpdatingQuote;
  const isBusy =
    isCalculating || isSavingQuote || isSubmittingQuote || isAddingQuoteToCart;

  useEffect(() => {
    if (!user) {
      return;
    }

    setFormValues((prevState) => ({
      ...prevState,
      customerInfo: {
        ...prevState.customerInfo,
        name: prevState.customerInfo.name || user?.name || "",
        email: prevState.customerInfo.email || user?.email || "",
      },
    }));
  }, [user]);

  /**
   * 更新客户信息字段。
   * @param {React.ChangeEvent<HTMLInputElement>} e 输入事件
   */
  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      customerInfo: {
        ...prevState.customerInfo,
        [name]: value,
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新运输信息字段。
   * @param {React.ChangeEvent<HTMLInputElement>} e 输入事件
   */
  const handleShippingChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      shipping: {
        ...prevState.shipping,
        [name]: value.toUpperCase(),
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新 PCB 规格字段。
   * @param {React.ChangeEvent<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>} e 输入事件
   */
  const handlePcbSpecChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      pcbSpec: {
        ...prevState.pcbSpec,
        [name]:
          ["differentDesignCount", "quantity", "layers", "thickness"].includes(
            name
          )
            ? Number(value)
            : value,
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新尺寸字段。
   * @param {React.ChangeEvent<HTMLInputElement>} e 输入事件
   */
  const handleSizeChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      pcbSpec: {
        ...prevState.pcbSpec,
        size: {
          ...prevState.pcbSpec.size,
          [name]: Number(value),
        },
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 更新高级选项开关。
   * @param {React.ChangeEvent<HTMLInputElement>} e 输入事件
   */
  const handleAdvancedOptionChange = (e) => {
    const { name, checked } = e.target;
    setFormValues((prevState) => ({
      ...prevState,
      pcbSpec: {
        ...prevState.pcbSpec,
        advancedOptions: {
          ...prevState.pcbSpec.advancedOptions,
          [name]: checked,
        },
      },
    }));
    setQuoteDirty(true);
  };

  /**
   * 记录当前上传的报价附件。
   * @param {React.ChangeEvent<HTMLInputElement>} e 文件选择事件
   */
  const handleAttachmentChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    setAttachments(selectedFiles);
    setQuoteDirty(true);
  };

  /**
   * 校验最基础的报价参数。
   * @returns {boolean} 是否通过校验
   */
  const validateQuoteForm = () => {
    if (!formValues.customerInfo.email.trim()) {
      toast.error("Please enter your email.");
      return false;
    }

    if (!formValues.shipping.countryCode.trim()) {
      toast.error("Please enter shipping country code.");
      return false;
    }

    if (
      Number(formValues.pcbSpec.size.length) <= 0 ||
      Number(formValues.pcbSpec.size.width) <= 0
    ) {
      toast.error("PCB size must be greater than 0.");
      return false;
    }

    if (Number(formValues.pcbSpec.quantity) <= 0) {
      toast.error("PCB quantity must be greater than 0.");
      return false;
    }

    return true;
  };

  /**
   * 生成报价请求负载。
   * @returns {Object} 标准报价请求
   */
  const buildQuotePayload = () => {
    return {
      ...formValues,
      sessionId,
      shipping: {
        ...formValues.shipping,
        countryCode: String(formValues.shipping.countryCode || "").toUpperCase(),
      },
    };
  };

  /**
   * 构建保存报价时使用的 FormData。
   * @returns {FormData} 表单数据
   */
  const buildQuoteFormData = () => {
    const nextFormData = new FormData();
    nextFormData.append("payload", JSON.stringify(buildQuotePayload()));

    attachments.forEach((file) => {
      nextFormData.append("attachments", file);
    });

    return nextFormData;
  };

  /**
   * 在线计算报价结果。
   */
  const handleCalculate = async () => {
    if (!validateQuoteForm()) {
      return;
    }

    try {
      const response = await calculateInstantQuote(buildQuotePayload()).unwrap();
      setQuoteResult(response?.quote || null);
      toast.success("Quote calculated successfully");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to calculate quote");
    }
  };

  /**
   * 保存或更新报价单。
   * @returns {Promise<Object|null>} 已保存的报价单
   */
  const handleSaveQuote = async () => {
    if (!validateQuoteForm()) {
      return null;
    }

    try {
      const formData = buildQuoteFormData();
      const response = savedQuote?._id
        ? await updateQuote({ id: savedQuote._id, formData }).unwrap()
        : await createQuote(formData).unwrap();
      const nextQuote = response?.quote || null;

      setSavedQuote(nextQuote);
      setQuoteResult(nextQuote);
      setQuoteDirty(false);
      toast.success(savedQuote?._id ? "Quote updated successfully" : "Quote saved successfully");
      return nextQuote;
    } catch (error) {
      toast.error(error?.data?.message || "Failed to save quote");
      return null;
    }
  };

  /**
   * 确保当前页面存在最新的已保存报价单。
   * @returns {Promise<Object|null>} 最新报价单
   */
  const ensureSavedQuote = async () => {
    if (!savedQuote?._id || quoteDirty) {
      return handleSaveQuote();
    }

    return savedQuote;
  };

  /**
   * 将报价单加入购物车，并跳转到购物车页面。
   */
  const handleAddToCart = async () => {
    const currentQuote = await ensureSavedQuote();
    if (!currentQuote?._id) {
      return;
    }

    try {
      const response = await addQuoteToCart(currentQuote._id).unwrap();
      dispatch(setCartItem(response?.cartItem));
      toast.success("Quote added to cart");
      navigate("/cart");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to add quote to cart");
    }
  };

  /**
   * 提交报价单到人工跟进流程。
   */
  const handleSubmitQuote = async () => {
    const currentQuote = await ensureSavedQuote();
    if (!currentQuote?._id) {
      return;
    }

    try {
      const response = await submitQuote({
        id: currentQuote._id,
        message: formValues.pcbSpec.specialRequest,
      }).unwrap();
      setSavedQuote(response?.quote || currentQuote);
      toast.success("Quote submitted for manual review");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to submit quote");
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
      <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-6">
        <div className="space-y-6">
          <section className="rounded-xl border border-gray-200 p-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formValues.customerInfo.name}
                  onChange={handleCustomerInfoChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formValues.customerInfo.email}
                  onChange={handleCustomerInfoChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={formValues.customerInfo.phone}
                  onChange={handleCustomerInfoChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Company</label>
                <input
                  type="text"
                  name="company"
                  value={formValues.customerInfo.company}
                  onChange={handleCustomerInfoChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Shipping Country Code *
                </label>
                <input
                  type="text"
                  name="countryCode"
                  maxLength={2}
                  value={formValues.shipping.countryCode}
                  onChange={handleShippingChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 uppercase"
                  placeholder="US"
                />
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 p-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              PCB Specification Selection
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Board Type</label>
                <select
                  name="boardType"
                  value={formValues.pcbSpec.boardType}
                  onChange={handlePcbSpecChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  {(config.boardTypes || []).map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Different Design In Panel
                </label>
                <input
                  type="number"
                  name="differentDesignCount"
                  min="1"
                  value={formValues.pcbSpec.differentDesignCount}
                  onChange={handlePcbSpecChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Length (mm) *</label>
                <input
                  type="number"
                  name="length"
                  min="1"
                  value={formValues.pcbSpec.size.length}
                  onChange={handleSizeChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Width (mm) *</label>
                <input
                  type="number"
                  name="width"
                  min="1"
                  value={formValues.pcbSpec.size.width}
                  onChange={handleSizeChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Quantity *</label>
                <input
                  type="number"
                  name="quantity"
                  min="1"
                  value={formValues.pcbSpec.quantity}
                  onChange={handlePcbSpecChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Layers</label>
                <select
                  name="layers"
                  value={formValues.pcbSpec.layers}
                  onChange={handlePcbSpecChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  {(config.layers || []).map((layer) => (
                    <option key={layer} value={layer}>
                      {layer} Layer{layer > 1 ? "s" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Material</label>
                <select
                  name="material"
                  value={formValues.pcbSpec.material}
                  onChange={handlePcbSpecChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  {(config.materials || []).map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">FR4-TG</label>
                <select
                  name="fr4Tg"
                  value={formValues.pcbSpec.fr4Tg}
                  onChange={handlePcbSpecChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  {(config.fr4TgOptions || []).map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Thickness (mm)</label>
                <select
                  name="thickness"
                  value={formValues.pcbSpec.thickness}
                  onChange={handlePcbSpecChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  {(config.thicknessOptions || []).map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Min Track / Spacing
                </label>
                <select
                  name="minTrackSpacing"
                  value={formValues.pcbSpec.minTrackSpacing}
                  onChange={handlePcbSpecChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  {(config.trackSpacingOptions || []).map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Min Hole Size</label>
                <select
                  name="minHoleSize"
                  value={formValues.pcbSpec.minHoleSize}
                  onChange={handlePcbSpecChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  {(config.holeSizeOptions || []).map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Solder Mask</label>
                <select
                  name="solderMask"
                  value={formValues.pcbSpec.solderMask}
                  onChange={handlePcbSpecChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  {(config.solderMaskOptions || []).map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Silkscreen</label>
                <select
                  name="silkscreen"
                  value={formValues.pcbSpec.silkscreen}
                  onChange={handlePcbSpecChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  {(config.silkscreenOptions || []).map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Surface Finish</label>
                <select
                  name="surfaceFinish"
                  value={formValues.pcbSpec.surfaceFinish}
                  onChange={handlePcbSpecChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  {(config.surfaceFinishOptions || []).map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Via Process</label>
                <select
                  name="viaProcess"
                  value={formValues.pcbSpec.viaProcess}
                  onChange={handlePcbSpecChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  {(config.viaProcessOptions || []).map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Finished Copper</label>
                <select
                  name="finishedCopper"
                  value={formValues.pcbSpec.finishedCopper}
                  onChange={handlePcbSpecChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  {(config.copperOptions || []).map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 p-4">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Customized Services and Advanced Options
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(config.advancedOptions || []).map((option) => (
                <label
                  key={option.key}
                  className="flex items-center gap-2 text-sm text-gray-800"
                >
                  <input
                    type="checkbox"
                    name={option.key}
                    checked={Boolean(
                      formValues.pcbSpec.advancedOptions[option.key]
                    )}
                    onChange={handleAdvancedOptionChange}
                  />
                  {option.label}
                </label>
              ))}
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">
                Gerber / Zip / Drawing Files
              </label>
              <input
                type="file"
                multiple
                onChange={handleAttachmentChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2"
              />
              {attachments.length > 0 ? (
                <p className="text-xs text-gray-500 mt-2">
                  {attachments.length} file(s) selected
                </p>
              ) : null}
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">
                Other Special Request
              </label>
              <textarea
                name="specialRequest"
                rows="4"
                value={formValues.pcbSpec.specialRequest}
                onChange={handlePcbSpecChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2"
                placeholder="Describe custom stackup, special marking, or manual review notes."
              />
            </div>
          </section>

          <section className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleCalculate}
              disabled={isBusy || isConfigLoading}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-md disabled:bg-gray-400"
            >
              {isCalculating ? "Calculating..." : "Calculate"}
            </button>
            <button
              type="button"
              onClick={handleSaveQuote}
              disabled={isBusy || isConfigLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md disabled:bg-gray-400"
            >
              {isSavingQuote ? "Saving..." : savedQuote?._id ? "Update Quote" : "Save Quote"}
            </button>
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={isBusy || isConfigLoading}
              className="bg-slate-900 hover:bg-black text-white font-semibold px-4 py-2 rounded-md disabled:bg-gray-400"
            >
              {isAddingQuoteToCart ? "Adding..." : "Add To Cart"}
            </button>
            <button
              type="button"
              onClick={handleSubmitQuote}
              disabled={isBusy || isConfigLoading}
              className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-4 py-2 rounded-md disabled:bg-gray-400"
            >
              {isSubmittingQuote ? "Submitting..." : "Submit RFQ"}
            </button>
          </section>
        </div>

        <aside className="rounded-xl border border-gray-200 p-4 bg-slate-50 h-fit">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Pricing Summary
          </h3>
          {quoteResult ? (
            <div className="space-y-3 text-sm text-gray-700">
              <div className="flex justify-between">
                <span>Base PCB Cost</span>
                <strong>${Number(quoteResult?.pricing?.basePcbCost || 0).toFixed(2)}</strong>
              </div>
              <div className="flex justify-between">
                <span>Advanced Options Cost</span>
                <strong>${Number(quoteResult?.pricing?.advancedOptionsCost || 0).toFixed(2)}</strong>
              </div>
              <div className="flex justify-between">
                <span>Engineering Fee</span>
                <strong>${Number(quoteResult?.pricing?.engineeringFee || 0).toFixed(2)}</strong>
              </div>
              <div className="flex justify-between">
                <span>Shipping</span>
                <strong>${Number(quoteResult?.pricing?.shippingCost || 0).toFixed(2)}</strong>
              </div>
              <div className="flex justify-between">
                <span>Lead Time</span>
                <strong>{quoteResult?.pricing?.leadTimeDays || 0} days</strong>
              </div>
              <div className="flex justify-between">
                <span>Shipping Method</span>
                <strong className="text-right max-w-[140px]">
                  {quoteResult?.pricing?.shippingMethod || "Pending"}
                </strong>
              </div>
              <div className="flex justify-between">
                <span>Estimated Weight</span>
                <strong>
                  {Number(quoteResult?.estimatedWeightGrams || 0).toFixed(2)} g
                </strong>
              </div>
              <div className="border-t border-gray-200 pt-3 flex justify-between text-lg text-gray-900">
                <span>Total</span>
                <strong>${Number(quoteResult?.pricing?.totalAmount || 0).toFixed(2)}</strong>
              </div>

              {savedQuote?.quoteNo ? (
                <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-blue-900">
                  Quote No: <strong>{savedQuote.quoteNo}</strong>
                </div>
              ) : null}

              {quoteResult?.reviewFlags?.length > 0 ? (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Review Flags</h4>
                  <ul className="list-disc pl-5 space-y-1 text-xs">
                    {quoteResult.reviewFlags.map((flag) => (
                      <li key={flag}>{flag}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {quoteResult?.warnings?.length > 0 ? (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Warnings</h4>
                  <ul className="list-disc pl-5 space-y-1 text-xs">
                    {quoteResult.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="text-sm text-gray-500 leading-6">
              Fill in the PCB parameters on the left and click <strong>Calculate</strong>.
              The system will estimate PCB cost, shipping fee, lead time, and
              weight for you.
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

export default FormGetQuote;
